import React from "react";

interface MemoryCardProps {
  image: string;
  className?: string;
  style?: React.CSSProperties;
}

export const MemoryCard: React.FC<MemoryCardProps> = ({ 
  image, 
  className = "", 
  style 
}) => {
  return (
    <div 
      className={`
        relative h-full w-full
        p-1 rounded-xl overflow-hidden
        bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500
        animate-gradient-xy
        shadow-2xl
        ${className}
      `}
      style={{
        ...style,
        backgroundSize: '200% 200%',
      }}
    >
      <div className="w-full h-full overflow-hidden rounded-lg bg-black">
        <img
          src={image}
          alt="Memory"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = `https://placehold.co/1200x800/6A5ACD/ffffff?text=Image+Loading`;
          }}
        />
      </div>
    </div>
  );
};

// Add this to your global CSS or Tailwind config
// @keyframes gradient-xy {
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// }
// .animate-gradient-xy { animation: gradient-xy 8s ease infinite; }

interface StackedMemoryGalleryProps {
  images: Array<{
    id: number | string;
    image: string;
  }>;
  onComplete?: () => void;
  className?: string;
}

const ROTATION_SEEDS = [-3, 2, -1, 3, -2, 1];

export const StackedMemoryGallery: React.FC<StackedMemoryGalleryProps> = ({ 
  images, 
  onComplete,
  className = "" 
}) => {
  const [currentMemoryIndex, setCurrentMemoryIndex] = React.useState(0);

  React.useEffect(() => {
    if (currentMemoryIndex >= images.length) {
      onComplete?.();
      return;
    }

    const timer = setTimeout(() => {
      setCurrentMemoryIndex(prev => prev + 1);
    }, 3000);

    return () => clearTimeout(timer);
  }, [currentMemoryIndex, images.length, onComplete]);

  const getCardStyle = (index: number): React.CSSProperties => {
    const rotation = ROTATION_SEEDS[index % ROTATION_SEEDS.length];
    
    if (index < currentMemoryIndex) {
      const pileOffset = (currentMemoryIndex - 1 - index) * 1;
      return {
        opacity: 0.6,
        transform: `scale(0.95) rotate(${rotation}deg) translate(${pileOffset * 6}px, ${pileOffset * 6}px)`,
        zIndex: 10 + index,
        boxShadow: '0 5px 10px rgba(0,0,0,0.3)',
        transition: 'all 1.5s ease',
      };
    }

    if (index === currentMemoryIndex) {
      return {
        opacity: 1,
        transform: `scale(1) rotate(${rotation}deg) translate(0px, 0px)`,
        zIndex: 10 + index + 10,
        boxShadow: '0 25px 50px -12px rgba(255, 105, 180, 0.8)',
        pointerEvents: 'auto',
        transition: 'all 1.5s ease',
      };
    }

    return {
      opacity: 0,
      transform: `rotate(${rotation}deg) scale(1)`,
      transition: 'all 1.5s ease',
      zIndex: 10 + index,
      pointerEvents: 'none',
    };
  };

  return (
    <div className={`relative mx-auto flex justify-center items-center min-h-[600px] w-full max-w-4xl ${className}`}>
      {images.map((memory, index) => (
        <div
          key={memory.id}
          className="absolute w-[90vw] max-w-[800px] h-[500px] origin-center"
          style={getCardStyle(index)}
        >
          <MemoryCard 
            image={memory.image}
            className="w-full h-full"
          />
        </div>
      ))}
    </div>
  );
};